// src/data/questions/physicalQuestions.ts
// Physical Safeguards Questions (Q21-Q25)

import { Question } from '../../types';

export const physicalQuestions: Question[] = [
  {
    id: 'Q21',
    section: 'physical',
    question: 'Do you have facility access controls (badge systems, visitor logs)?',
    type: 'single',
    options: [
      { value: 'comprehensive', label: 'Yes, comprehensive access controls with monitoring', description: '', points: 3 },
      { value: 'basic', label: 'Basic controls (locks, sign-in sheets)', description: '', points: 2 },
      { value: 'no', label: 'No formal access controls', description: '', points: 0 },
      { value: 'na', label: 'Not applicable', description: '', points: 0 },
    ],
    regulation: '§164.310(a)(1) - Facility access controls',
    required: false,
    conditionalOn: { questionId: 'Q3', requiredValue: ['yes', 'hybrid'] },
    weight: 2,
    tooltipContent: {
      plainEnglish: "Facility access controls prevent unauthorized people from entering areas where PHI is stored or processed. This includes reception areas (where records are kept), server rooms (where data is backed up), and clinical spaces (where clinicians access patient information). Controls might be simple (visitor sign-in sheets, locked doors, keys) or sophisticated (badge readers, CCTV, tailgating prevention). For a small clinic, a visitor log and locked cabinet may suffice. For a hospital, badge systems, video surveillance, and access audit logs are necessary. The key principle is: you know who has been in the facility at any given time, you can trace if an unauthorized person entered, and you have processes to prevent unauthorized access.",
      hipaaReference: "§164.310(a)(1) - Facility Access Controls (visitor controls, facility security plan, access authorization and supervision). Status: Required if you have physical facilities. Effective Date: Physical Safeguards Rule (2003). 12% of healthcare breaches involve physical unauthorized access. Inadequate access controls are cited in ~30% of audits of organizations with physical facilities.",
      realWorldExample: "A small clinic has a visitor log at the front desk: date, name, purpose, time in/out. All visitors must check in. Records are stored in a locked cabinet in a back office (key held by office manager). CCTV covers the front entrance. A hospital has a badge system: all employees get badges, contractors get temporary badges (1-week validity), visitors must be escorted by staff. Badge readers log all entries/exits. Server room has separate badging; access limited to IT and security. A clinic discovers someone has been taking trash bags from the dumpster outside (containing discarded notes with PHI). They add a fence and locked dumpster enclosure.",
      estimatedCost: {
        small: "$500-2,000 (visitor log + locks)",
        medium: "$10,000-30,000 (badge system + basic CCTV)",
        large: "$50,000-150,000+ (comprehensive badge + CCTV + tailgating prevention)"
      },
      implementationSteps: [
        "Assessment: 1-2 weeks",
        "Implementation: 4-8 weeks",
        "Staff training: 1 week"
      ],
      commonMistakes: [
        "No visitor log or tracking",
        "Badges not revoked when employees leave",
        "No CCTV or access logs",
        "Unlocked areas with PHI"
      ],
      resourceLinks: [
        { title: "HHS Physical Safeguards", url: "https://www.hhs.gov/hipaa/for-professionals/security/laws-regulations/index.html", type: "external" }
      ],
      timeToComplete: {
        small: "4-6 weeks",
        medium: "6-10 weeks",
        large: "10-14 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "Facilities manager",
        "Security director",
        "Office manager",
        "IT security"
      ],
      whereToLook: [
        "Visitor logs",
        "Badge system access logs",
        "CCTV footage retention policy",
        "Facility security policy"
      ],
      howToVerify: "Request visitor logs for the last month. Check if badge system logs all entries/exits. Review CCTV coverage. Ask: 'How do you prevent unauthorized access? What happens if someone loses their badge?'",
      whatToDocument: "Access control methods (visitor log, badges, CCTV), badge revocation process, visitor log retention, CCTV retention period"
    }
  },
  {
    id: 'Q22',
    section: 'physical',
    question: 'Do you have policies governing workstation use (location, security)?',
    type: 'single',
    options: [
      { value: 'documented', label: 'Yes, documented policies for all workstation types', description: '', points: 3 },
      { value: 'informal', label: 'Informal guidelines', description: '', points: 1 },
      { value: 'no', label: 'No formal policies', description: '', points: 0 },
      { value: 'na', label: 'Not applicable', description: '', points: 0 },
    ],
    regulation: '§164.310(b) - Workstation use (REQUIRED)',
    required: false,
    conditionalOn: { questionId: 'Q3', requiredValue: ['yes', 'hybrid'] },
    weight: 2,
    tooltipContent: {
      plainEnglish: "Workstation use policies define how employees can use computers/devices to access PHI. This includes: (1) where workstations can be used (office only, or also home/public places?), (2) security requirements (screen locks, privacy, VPN for remote access), (3) what systems are approved, and (4) consequences for policy violations. For example: 'EHR access is permitted only in secure office locations with privacy screens. Remote access via VPN is allowed only with MFA. Use of personal computers for PHI access is prohibited.' Many organizations have no policy. Employees use personal laptops in coffee shops, access records on unencrypted phones, or share login credentials. Without a policy, you have no basis to discipline violations and OCR will cite you for lack of controls.",
      hipaaReference: "§164.310(b) - Workstation Use (REQUIRED); defines permitted uses and functions. Status: Required. Effective Date: Physical Safeguards Rule (2003). 40% of healthcare breaches involve unsecured workstations or remote access without VPN. OCR cites missing workstation use policies in ~50% of audits.",
      realWorldExample: "A clinic implements a policy: 'EHR access is permitted only in the clinic office. Accessing from home, coffee shops, or public places is prohibited. Remote access is not allowed. Any employee found accessing EHR from outside the office will face disciplinary action.' A hospital allows remote access via VPN + MFA. Policy states: 'Remote EHR access requires VPN and MFA. You must be in a private location with a privacy screen. You may not access from public places.' A clinic allows BYOD with a policy: 'Personal devices may access EHR only with device management software installed, requiring device encryption, screen lock, and regular security patches.'",
      estimatedCost: {
        small: "$1,000-3,000 (policy development)",
        medium: "$5,000-20,000 (VPN/remote access setup)",
        large: "$10,000-30,000 (MDM + VPN + policy)"
      },
      implementationSteps: [
        "Policy development: 2 weeks",
        "Implementation: 2-4 weeks",
        "Training and enforcement: 1-2 weeks"
      ],
      commonMistakes: [
        "No policy at all",
        "Allowing access from public WiFi",
        "No VPN for remote access",
        "Personal devices without MDM"
      ],
      resourceLinks: [
        { title: "HHS Workstation Use", url: "https://www.hhs.gov/hipaa/for-professionals/security/laws-regulations/index.html", type: "external" }
      ],
      timeToComplete: {
        small: "3-4 weeks",
        medium: "4-6 weeks",
        large: "6-8 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "IT Director",
        "HIPAA Security Official",
        "HR (for policy enforcement)",
        "Remote workers"
      ],
      whereToLook: [
        "Workstation use policy document",
        "Acceptable use policy (AUP)",
        "VPN access logs",
        "MDM enrollment records"
      ],
      howToVerify: "Request the workstation use policy. Check if it defines: approved locations, approved devices, security requirements (VPN, MFA, screen locks). Ask: 'Can employees access PHI from home? From coffee shops? What security is required?'",
      whatToDocument: "Policy existence, approved access locations, VPN/MFA requirements, BYOD policy, enforcement procedures"
    }
  },
  {
    id: 'Q23',
    section: 'physical',
    question: 'Do you have physical safeguards for workstations (screen privacy, locks)?',
    type: 'single',
    options: [
      { value: 'comprehensive', label: 'Yes, privacy screens, locked rooms, automatic screen locks', description: '', points: 3 },
      { value: 'basic', label: 'Basic safeguards (password-protected)', description: '', points: 1 },
      { value: 'no', label: 'No physical safeguards', description: '', points: 0 },
      { value: 'na', label: 'Not applicable', description: '', points: 0 },
    ],
    regulation: '§164.310(c) - Workstation security (REQUIRED)',
    required: false,
    conditionalOn: { questionId: 'Q3', requiredValue: ['yes', 'hybrid'] },
    weight: 2,
    tooltipContent: {
      plainEnglish: "Physical workstation safeguards prevent unauthorized viewing of PHI on monitors or keyboards. This includes: (1) privacy screens (reduces viewing angle so only the person in front of the monitor sees it), (2) screen positioning (away from hallways/public areas), (3) screen locks (automatic logoff after inactivity), (4) locked workstations (physical security to prevent unauthorized use), and (5) protected keyboards (prevent keylogging devices). A clinician's monitor visible to everyone walking through the hallway showing patient names and medical information = violation. A receptionist steps away from their desk for lunch with the EHR still logged in and a visitor accesses records = violation. An unlocked laptop in a hospital hallway = breach waiting to happen.",
      hipaaReference: "§164.310(c) - Workstation Security (REQUIRED); includes physical and electronic security. Status: Required. Effective Date: Physical Safeguards Rule (2003). Visual snooping is the most common low-level breach. Unattended workstations are specifically targeted by threat actors. Privacy screen safeguards are cheap ($50-200/monitor) but save millions in breach costs.",
      realWorldExample: "A clinic installs privacy screens on all workstations in the reception and clinical areas. Screens cost ~$100 each; with 30 workstations, total cost is $3,000. Monitors are also positioned away from the hallway/waiting room. A hospital discovers a clinician has left their workstation unattended with the EHR logged in. They implement automatic screen lock (15 minutes) and add signage reminding staff to lock before leaving. Another clinic has monitors visible from the street (external window). They reposition monitors or add privacy screens.",
      estimatedCost: {
        small: "$1,500-6,000 (privacy screens for 30 monitors)",
        medium: "$5,000-15,000 (privacy screens + repositioning + locks)",
        large: "$10,000-30,000 (comprehensive physical security)"
      },
      implementationSteps: [
        "Assessment and procurement: 1-2 weeks",
        "Installation: 1-2 days",
        "Configuration (screen locks): 1 week",
        "Training: 1 week"
      ],
      commonMistakes: [
        "No privacy screens",
        "Monitors visible from public areas",
        "No automatic screen locks",
        "Workstations left unattended and logged in"
      ],
      resourceLinks: [
        { title: "HHS Workstation Security", url: "https://www.hhs.gov/hipaa/for-professionals/security/laws-regulations/index.html", type: "external" }
      ],
      timeToComplete: {
        small: "2-3 weeks",
        medium: "3-4 weeks",
        large: "4-6 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "IT Director",
        "Facilities manager",
        "Department managers",
        "Workstation users"
      ],
      whereToLook: [
        "Privacy screen inventory",
        "Screen lock configuration (Group Policy)",
        "Workstation positioning",
        "Physical security devices (cable locks)"
      ],
      howToVerify: "Walk through the facility. Check: Are privacy screens installed? Are monitors positioned away from public view? Are screen locks enabled (test by leaving a workstation idle)? Are workstations physically secured?",
      whatToDocument: "Privacy screen installation status, screen lock timeout settings, monitor positioning, physical security measures"
    }
  },
  {
    id: 'Q24',
    section: 'physical',
    question: 'How do you handle disposal of devices/media containing ePHI?',
    type: 'single',
    options: [
      { value: 'certified', label: 'Certified destruction/wiping with documentation', description: '', points: 3 },
      { value: 'basic', label: 'Basic deletion (not certified)', description: '', points: 1 },
      { value: 'no', label: 'No formal disposal procedures', description: '', points: 0 },
      { value: 'unsure', label: 'I don\'t know', description: '', points: 0, isUnknown: true },
    ],
    regulation: '§164.310(d)(1) - Device and media controls - Disposal (REQUIRED)',
    required: true,
    weight: 3,
    tooltipContent: {
      plainEnglish: "When you dispose of a device or storage media that contained PHI (computers, laptops, hard drives, USB drives, tapes, phones), you must destroy it securely so the data can't be recovered. 'Throwing it in the trash' or 'wiping it with delete' isn't sufficient; hard drive recovery tools can retrieve 'deleted' data. You need certified destruction: physical destruction (shredding, incineration) or certified data wiping (using standards like NIST 800-88). You must maintain documentation: what device, when disposed, how disposed, by whom, and with what certification. This is a compliance requirement and provides evidence if a device is later found with PHI.",
      hipaaReference: "§164.310(d)(1) - Device and Media Controls; Disposal (REQUIRED). Status: Required. Effective Date: Physical Safeguards Rule (2003). 15% of healthcare breaches involve recovered data from improperly disposed devices. Devices thrown in dumpsters or sold at electronics auctions have been recovered with PHI. Failure to document disposal is cited in ~40% of audits.",
      realWorldExample: "A clinic buys a new server. The old server is donated to a school without wiping the data. The school resells it years later. Someone recovers the old clinic's patient records. Breach discovered, OCR investigation. Massive liability. Clinic should have hired certified destruction vendor (~$500-1,000 to securely wipe/destroy the server). A hospital has a device disposal policy: all devices that touched PHI must be inventoried and sent to a certified destruction vendor. IT tracks this: device serial number, PHI? (yes/no), disposal date, vendor, certificate of destruction filed.",
      estimatedCost: {
        small: "$50-500 per device",
        medium: "$500-2,000/year (annual contracts)",
        large: "$2,000-10,000/year (enterprise e-waste program)"
      },
      implementationSteps: [
        "Vendor selection: 1-2 weeks",
        "Policy development: 1 week",
        "Inventory system setup: 1 week",
        "Initial device disposal project: 4-8 weeks"
      ],
      commonMistakes: [
        "Throwing devices in trash",
        "Donating devices without wiping",
        "No documentation of disposal",
        "Using non-certified destruction vendors"
      ],
      resourceLinks: [
        { title: "NIST SP 800-88 Media Sanitization", url: "https://csrc.nist.gov/publications/detail/sp/800-88/rev-1/final", type: "external" }
      ],
      timeToComplete: {
        small: "2-4 weeks",
        medium: "4-6 weeks",
        large: "6-10 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "IT Director",
        "Asset management",
        "Procurement",
        "Certified destruction vendor"
      ],
      whereToLook: [
        "Device disposal policy",
        "Destruction certificates",
        "Device inventory/tracking system",
        "Vendor contracts"
      ],
      howToVerify: "Request the device disposal policy. Ask: 'Show me destruction certificates for devices disposed in the last year.' Check if all devices with PHI are tracked and properly destroyed. Review vendor certifications.",
      whatToDocument: "Disposal policy, number of devices disposed, destruction certificates on file, vendor certifications"
    }
  },
  {
    id: 'Q25',
    section: 'physical',
    question: 'Do you sanitize/wipe media before reuse?',
    type: 'single',
    options: [
      { value: 'documented', label: 'Yes, documented sanitization procedures', description: '', points: 3 },
      { value: 'informal', label: 'Informal wiping', description: '', points: 1 },
      { value: 'no', label: 'No reuse procedures', description: '', points: 0 },
      { value: 'noreuse', label: 'We don\'t reuse media', description: '', points: 3 },
    ],
    regulation: '§164.310(d)(2) - Media re-use (REQUIRED)',
    required: true,
    weight: 2,
    tooltipContent: {
      plainEnglish: "Before reusing storage media (hard drives, USB drives, optical media, tapes), you must sanitize or wipe the media to ensure old data can't be recovered. Similar to device disposal, but for reuse instead of destruction. Wiping must follow standards (NIST 800-88) and should be documented. Best practice: most organizations don't reuse media; they destroy it and buy new. But if you reuse, you must have a verified sanitization process. This is a less common issue now (most organizations use cloud storage instead of physical media), but still applies if you reuse USB drives, hard drives, or tapes.",
      hipaaReference: "§164.310(d)(2) - Media Re-use (REQUIRED). Status: Required. Effective Date: Physical Safeguards Rule (2003). Reuse without sanitization is explicitly prohibited; any breach from reused media with old PHI = violation. This is easy to verify (and easy to violate): have a media reuse policy with wiping procedures.",
      realWorldExample: "A clinic has a policy: 'Media is never reused. All devices are destroyed when no longer needed.' Simple and compliant. Another clinic has a media reuse policy: 'Portable drives can be reused if wiped using DBAN or manufacturer secure erase. Verification of wipe is documented (screenshot of completion report). Wiped drives are tracked in inventory and available for reuse.' A hospital discovers a USB drive in lost and found with old patient data that was supposedly wiped but wasn't. Investigation shows the IT person who wiped it didn't follow procedures correctly. They retrain the person and implement verification process (wipe performed by one person, verified by second person).",
      estimatedCost: {
        small: "$0-1,000 (wiping software)",
        medium: "$200-500/year (verification/documentation)",
        large: "$50-500 per device (destruction instead of reuse)"
      },
      implementationSteps: [
        "Policy development: 1 week",
        "Process setup: 1 week",
        "Staff training: 1-2 days"
      ],
      commonMistakes: [
        "Reusing media without wiping",
        "Using simple 'delete' instead of secure wipe",
        "No verification that wipe was successful",
        "No documentation of sanitization"
      ],
      resourceLinks: [
        { title: "NIST SP 800-88 Media Sanitization", url: "https://csrc.nist.gov/publications/detail/sp/800-88/rev-1/final", type: "external" }
      ],
      timeToComplete: {
        small: "1-2 weeks",
        medium: "2-3 weeks",
        large: "3-4 weeks"
      }
    },
    investigationGuide: {
      whoToAsk: [
        "IT Director",
        "System administrators",
        "Asset management",
        "Staff who handle media"
      ],
      whereToLook: [
        "Media reuse policy",
        "Sanitization logs",
        "Wiping tool documentation",
        "Media inventory"
      ],
      howToVerify: "Request the media reuse policy. Ask: 'Do you reuse storage media? If yes, how do you sanitize it? Show me sanitization logs.' Check if wiping follows NIST 800-88 standards. Verify documentation exists.",
      whatToDocument: "Reuse policy (or no-reuse policy), sanitization procedures, wiping tool used, verification process, sanitization logs"
    }
  },
];
